
import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { supabase } from "@/integrations/supabase/client";
import { LayoutGrid } from "lucide-react";

type Board = {
  id: string;
  name: string;
  emoji: string;
};

type BoardSelectorProps = {
  companyId?: string;
  selectedBoardId: string | null;
  onBoardSelect: (boardId: string | null) => void;
};

const BoardSelector = ({ companyId, selectedBoardId, onBoardSelect }: BoardSelectorProps) => {
  const [boards, setBoards] = useState<Board[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchBoards = async () => {
      setIsLoading(true);
      
      if (!companyId) {
        console.log("No company ID provided to BoardSelector");
        setIsLoading(false);
        setBoards([]);
        return;
      }

      try {
        console.log("Fetching boards for company ID:", companyId);
        const { data, error } = await supabase
          .from("feedback_boards")
          .select("id, name, emoji")
          .eq("company_id", companyId);

        if (error) {
          console.error("Error fetching boards:", error);
          setBoards([]);
          return;
        }

        console.log("Fetched boards:", data);
        setBoards(data || []);
      } catch (error) {
        console.error("Error in fetchBoards:", error);
        setBoards([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchBoards();
  }, [companyId]);

  if (isLoading) {
    return (
      <Card className="p-4">
        <h3 className="font-medium mb-4">Feedback Boards</h3>
        <div className="text-sm text-muted-foreground">Loading boards...</div>
      </Card>
    );
  }

  return (
    <Card className="p-4">
      <h3 className="font-medium mb-4">Feedback Boards</h3>
      <ScrollArea className="h-[300px] md:h-[400px]">
        <div className="space-y-2">
          <div
            className={`p-3 rounded-lg cursor-pointer transition-colors ${
              selectedBoardId === null
                ? "bg-primary/10 hover:bg-primary/15"
                : "hover:bg-accent"
            }`}
            onClick={() => onBoardSelect(null)}
          >
            <div className="flex items-center space-x-2">
              <span className="text-2xl">🔍</span>
              <span className="font-medium">All Boards</span>
            </div>
          </div>
          
          {boards && boards.length > 0 ? (
            boards.map((board) => (
              <div
                key={board.id}
                className={`p-3 rounded-lg cursor-pointer transition-colors ${
                  board.id === selectedBoardId
                    ? "bg-primary/10 hover:bg-primary/15"
                    : "hover:bg-accent"
                }`}
                onClick={() => onBoardSelect(board.id)}
              >
                <div className="flex items-center space-x-2">
                  <span className="text-2xl">{board.emoji || "📋"}</span>
                  <span className="font-medium">{board.name}</span>
                </div>
              </div>
            ))
          ) : (
            <div className="p-3 text-sm text-muted-foreground flex flex-col items-center justify-center">
              <LayoutGrid className="h-8 w-8 mb-2 text-muted-foreground/50" />
              <p>No boards found</p>
              <p className="text-xs mt-1">Create boards in the Boards tab</p>
            </div>
          )}
        </div>
      </ScrollArea>
    </Card>
  );
};

export default BoardSelector;
